/**
 * TARİH VE SÜRE BİÇİMLEME.
 *
 * Sunucudan gelen tarihler ISO 8601 METİNDİR. `new Date(...)` ile
 * ayrıştırıldığında TARAYICININ yerel saat dilimine çevrilir; bu, saat
 * içeren damgalarda (basma zamanı) doğru davranıştır ama SALT TARİH
 * alanlarında (`2026-09-01`) yanlıştır: UTC gece yarısı Türkiye'de aynı gün
 * 03:00'tür, ama negatif ofsetli bir tarayıcıda BİR ÖNCEKİ GÜN görünür.
 *
 * Bu yüzden salt tarih alanları AYRIŞTIRILMADAN, metin olarak kesilir.
 */

/** `2026-09-01T00:00:00.000Z` → `2026-09-01`. Ayrıştırma YAPMAZ. */
export function isoGun(damga: string): string {
  return damga.slice(0, 10);
}

const GUN_KISA = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const;

/** ISO gün numarasından (1–7) kısa ad. Aralık dışı değer boş döner. */
export function gunAdi(isoGunNo: number): string {
  return GUN_KISA[isoGunNo - 1] ?? '';
}

/** `2026-09-01T…` → `01.09`. Izgara başlığı için. */
export function gunAy(damga: string): string {
  const [, ay, gun] = isoGun(damga).split('-');
  return gun !== undefined && ay !== undefined ? `${gun}.${ay}` : '';
}

/** `2026-09-01T…` → `01.09.2026`. */
export function tarihMetni(damga: string | null): string {
  if (damga === null) return '—';
  const [yil, ay, gun] = isoGun(damga).split('-');
  return gun !== undefined && ay !== undefined && yil !== undefined ? `${gun}.${ay}.${yil}` : '—';
}

/**
 * GERÇEK BİR ANI tarih + saat olarak gösterir.
 *
 * `tarihMetni`nin AKSİNE burada ayrıştırmak DOĞRUDUR: bu değerler bir zaman
 * damgası (oturum son kullanım, bildirim zamanı) ve kullanıcı onu kendi
 * saatiyle okumalı. `tarihMetni` metni keser ve saati hiç göstermez.
 *
 * `Cihazlarim` bu işlevi 0.30.0'a kadar KENDİ İÇİNDE yazıyordu. Panelde aynı
 * hatanın ikincisiydi: `PersonelKarti`nin yerel kopyası salt tarih alanlarına
 * uygulanınca gün kaydırıyordu (B1). Biçimleyiciler tek dosyada tutulur.
 */
export function zamanMetni(damga: string | null): string {
  if (damga === null) return '—';

  const an = new Date(damga);
  if (Number.isNaN(an.getTime())) return '—';

  return new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }).format(an);
}

/**
 * Saat damgasını `HH:MM` yapar.
 *
 * Burada YEREL SAATE çevirmek doğrudur: bu değerler gerçek bir ana ait
 * (basma zamanı) ve kullanıcı onu kendi saatiyle okumalı. Sunucu
 * `Europe/Istanbul` ile çalışıyor; panel de aynı bölgede.
 */
export function saatMetni(damga: string | null): string {
  if (damga === null) return '—';
  const an = new Date(damga);
  if (Number.isNaN(an.getTime())) return '—';
  return an.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

/** Dakikayı `7s 30d` yapar. Negatif değer korunur (sapma gösterimi). */
export function sureMetni(dakika: number | null): string {
  if (dakika === null) return '—';
  const isaret = dakika < 0 ? '-' : '';
  const mutlak = Math.abs(dakika);
  const saat = Math.floor(mutlak / 60);
  const kalan = mutlak % 60;
  if (saat === 0) return `${isaret}${kalan}d`;
  return kalan === 0 ? `${isaret}${saat}s` : `${isaret}${saat}s ${kalan}d`;
}

/** Bugünün `YYYY-MM-DD` biçimi — YEREL, `toISOString()` DEĞİL. */
export function bugun(): string {
  return gunEkle(new Date(), 0);
}

/**
 * Tarihe gün ekler ve `YYYY-MM-DD` döndürür.
 *
 * `toISOString()` KULLANILMAZ: UTC'ye çevirir ve Türkiye'de gece yarısından
 * önceki saatlerde bir önceki günü verir. Yerel bileşenler elle birleşiyor.
 */
export function gunEkle(taban: Date | string, gun: number): string {
  const an = typeof taban === 'string' ? new Date(`${isoGun(taban)}T00:00:00`) : new Date(taban);
  an.setDate(an.getDate() + gun);

  const yil = an.getFullYear();
  const ay = String(an.getMonth() + 1).padStart(2, '0');
  const gunNo = String(an.getDate()).padStart(2, '0');
  return `${yil}-${ay}-${gunNo}`;
}

/**
 * Verilen tarihi içeren haftanın PAZARTESİSİ.
 *
 * Plan dönemi hafta başında başlar; rastgele bir güne demirlenirse 4 haftalık
 * ufuk hafta ortasında kesilir ve rotasyon deseni okunamaz hâle gelir.
 */
export function haftaBasi(tarih: string): string {
  const an = new Date(`${isoGun(tarih)}T00:00:00`);
  // getDay(): 0 Pazar … 6 Cumartesi. Pazar'ı haftanın SONU sayıyoruz.
  const kaydir = an.getDay() === 0 ? -6 : 1 - an.getDay();
  return gunEkle(an, kaydir);
}

/** İki tarih arası gün listesi (uçlar dahil). */
export function gunAraligi(baslangic: string, bitis: string): string[] {
  const gunler: string[] = [];
  let simdiki = isoGun(baslangic);
  const son = isoGun(bitis);

  // Üst sınır: bozuk aralıkta sonsuz döngü yerine kesilmiş liste yeğdir.
  for (let sayac = 0; simdiki <= son && sayac < 400; sayac += 1) {
    gunler.push(simdiki);
    simdiki = gunEkle(simdiki, 1);
  }

  return gunler;
}
